import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { ReduxState } from 'services/types';
import { OrderObject, Duration, Languages, OrderProduct } from 'common/types';
import { Theme, Radio, Box, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import moment from 'moment-timezone';
import {
	getDurationString,
	getLatestProductEndTime,
	getTimeNearestToNextQuarterHour,
	getNewEndTime,
	getTotalRentalDurationFromProducts,
} from 'common/utils/dateCalculations';
import * as RentalSelectors from 'selectors/RentalViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization';
import { localFormatString } from 'common/utils/dateUtils';
import BackdropDialog from 'common/components/BackdropDialog';
import { orderHasDeliveryOrPickup } from 'common/modules/delivery/utils';

interface OwnProps {
	onClose: (startAndEndTimeChanges?: StartAndEndTimeChanges) => void;
	open: boolean;
	rental: OrderObject;
}

interface StateToProps {
	lang: Languages;
}

interface StartAndEndTimeChanges {
	keepOldStartTime?: boolean;
	keepOldEndTime?: boolean;
	success?: boolean;
}

type Props = StateToProps & OwnProps & WithStyles<typeof styles>;

const EarlyOrLateDialog = (props: Props) => {
	const { classes, open, onClose, rental } = props;
	const [keepOldEndTime, setKeepOldEndTime] = useState<boolean>(true);
	const { t } = useTranslation();
	const rentalEarly = moment(rental.rentalInfo.startDate).diff(moment(), 'seconds');
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);
	const openingHoursWithExceptions = useSelector(RentalSelectors.openingHoursWithExceptions);
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const dateFormatShort = localFormatString('HH:mm', shopDateFormat);
	const dateFormatWeek = localFormatString('dddd HH:mm', shopDateFormat);
	const dateFormatLong = localFormatString('DD.MM.YYYY, HH:mm', shopDateFormat);

	const rentalHasDelivery = orderHasDeliveryOrPickup(rental.orderDelivery);

	const LocaleSpec: moment.CalendarSpec = {
		sameDay: `${dateFormatShort} ([${t('common:times.today').toLowerCase()}])`,
		nextDay: `${dateFormatShort} ([${t('common:times.tomorrow').toLowerCase()}])`,
		nextWeek: dateFormatWeek,
		lastDay: `${dateFormatShort} ([${t('common:times.yesterday').toLowerCase()}])`,
		lastWeek: dateFormatLong,
		sameElse: dateFormatLong,
	};

	const differenceDurationObject: Duration = {
		durationInSeconds: Math.abs(rentalEarly),
		durationType: '24h',
		durationName: null,
	};

	const durationToEvent = getDurationString(differenceDurationObject, 'short', props.lang, t);

	const handleUpdateTime = (keep: boolean) => {
		setKeepOldEndTime(keep);
	};

	const handleClose = async () => {
		onClose({
			keepOldStartTime: rentalHasDelivery,
			keepOldEndTime: keepOldEndTime ?? false,
			success: true,
		});
	};

	const renderEndTimeSelector = () => {
		let exampleShopperName: string | undefined;
		let exampleShopperProducts: OrderProduct[] = [];
		const shopperNamesWithEndTimes = rental.shoppers.map((s) => {
			const shopperProducts = rental.products.filter((p) => p.shopperId === s.id);
			if (!exampleShopperName || !exampleShopperProducts.length) {
				exampleShopperName = s.firstName;
				exampleShopperProducts = shopperProducts;
			}
			const returnObject = {
				[s.firstName]: getLatestProductEndTime(shopperProducts) || moment().toISOString(),
			};
			return returnObject;
		});
		const uniqueEndTimes = [...new Set(shopperNamesWithEndTimes.map((s) => Object.values(s)[0]))];
		const exampleEndTime =
			getLatestProductEndTime(exampleShopperProducts) || moment().toISOString();
		const multipleTimes = uniqueEndTimes.length > 1 && Object.keys(shopperNamesWithEndTimes[0])[0];
		const updatedExampleEndTime = moment(
			getNewEndTime(
				getTimeNearestToNextQuarterHour(moment()).toISOString(),
				getTotalRentalDurationFromProducts(exampleShopperProducts),
				openingHoursWithExceptions,
				shopPublicInfo?.timeZone,
			),
		);

		const keepOriginalEndtimeTexts =
			rentalEarly > 0
				? multipleTimes
					? {
							primary: t(
								'earlyOrLate.updateStartTimeToCurrentTime',
								'Update start time to current time',
							),
							secondary: t('earlyOrLate.exampleShopperEndTime', {
								endTime: moment(exampleEndTime).calendar(moment(), LocaleSpec),
								shopperName: exampleShopperName,
								defaultValue: 'E.g. End time for {{shopperName}} will be {{endTime}}',
							}),
					  }
					: {
							primary: t('earlyOrLate.keepOldEndTime', {
								endTime: moment(exampleEndTime).calendar(moment(), LocaleSpec),
								defaultValue: 'Keep original end time {{endTime}}',
							}),
					  }
				: {
						primary: t('earlyOrLate.keepOldTime', 'Keep original time'),
						secondary: t('earlyOrLate.exampleTime', {
							startTime: moment(rental.rentalInfo.startDate).calendar(moment(), LocaleSpec),
							endTime: moment(exampleEndTime).calendar(moment(), LocaleSpec),
							defaultValue: 'E.g. {{startTime}} - {{endTime}}',
						}),
				  };
		const updateStartAndEndTimeTexts =
			rentalEarly > 0
				? multipleTimes
					? {
							primary: t('earlyOrLate.startAndEndTimes', 'Update start and end times'),
							secondary: t('earlyOrLate.exampleShopperEndTime', {
								endTime: updatedExampleEndTime.calendar(moment(), LocaleSpec),
								shopperName: exampleShopperName,
								defaultValue: 'E.g. end time for {{shopperName}} will be {{endTime}}',
							}),
					  }
					: {
							primary: t('earlyOrLate.updateEndTime', {
								endTime: updatedExampleEndTime.calendar(moment(), LocaleSpec),
								defaultValue: 'Update end time to {{endTime}}',
							}),
					  }
				: {
						primary: t('earlyOrLate.updateStartAndEndTime', 'Update start and end time'),
						secondary: t('earlyOrLate.exampleTime', {
							startTime: getTimeNearestToNextQuarterHour().calendar(moment(), LocaleSpec),
							endTime: updatedExampleEndTime.calendar(moment(), LocaleSpec),
							defaultValue: 'E.g. {{startTime}} - {{endTime}}',
						}),
				  };

		return (
			<Box>
				<List>
					{rentalHasDelivery && (
						<ListItem button>
							<ListItemAvatar>
								<Radio color="primary" checked={rentalHasDelivery} />
							</ListItemAvatar>
							<ListItemText
								primary={t(
									'earlyOrLate.keepOriginalStartAndEndtime',
									'Keep original start and end time',
								)}
							/>
						</ListItem>
					)}
					<ListItem button onClick={(e) => handleUpdateTime(true)} disabled={rentalHasDelivery}>
						<ListItemAvatar>
							<Radio
								color="primary"
								checked={keepOldEndTime && !rentalHasDelivery}
								disabled={rentalHasDelivery}
							/>
						</ListItemAvatar>
						<ListItemText
							className={rentalHasDelivery ? classes.grey : undefined}
							primary={keepOriginalEndtimeTexts.primary}
							secondary={keepOriginalEndtimeTexts.secondary}
						/>
					</ListItem>
					<ListItem button onClick={(e) => handleUpdateTime(false)} disabled={rentalHasDelivery}>
						<ListItemAvatar>
							<Radio
								color="primary"
								checked={!keepOldEndTime && !rentalHasDelivery}
								disabled={rentalHasDelivery}
							/>
						</ListItemAvatar>
						<ListItemText
							className={rentalHasDelivery ? classes.grey : undefined}
							primary={updateStartAndEndTimeTexts.primary}
							secondary={updateStartAndEndTimeTexts.secondary}
						/>
					</ListItem>
				</List>
			</Box>
		);
	};

	return (
		<BackdropDialog
			isOpen={open}
			onClose={() => onClose()}
			label={
				rentalEarly > 0
					? t('earlyOrLate.aboutToStartEarly', {
							duration: durationToEvent,
							defaultValue: `You are about to start {{duration}} early`,
					  })
					: t('earlyOrLate.aboutToStartLate', {
							duration: durationToEvent,
							defaultValue: `You are about to start {{duration}} late`,
					  })
			}
			body={renderEndTimeSelector()}
			confirm={{
				label: t('common:actions.start'),
				callback: handleClose,
				closeAfterDone: true,
			}}
			cancel={{ label: t('common:actions.cancel') }}
		/>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		paper: {
			margin: 0,
			[theme.breakpoints.up('sm')]: {
				width: 460,
			},
		},
		title: {
			paddingTop: theme.spacing(5),
			textAlign: 'center',
		},
		content: {
			display: 'grid',
			padding: theme.spacing(1, 4),
		},
		paddingActions: {
			padding: theme.spacing(1, 3, 3),
		},
		selectPadding: {
			paddingRight: theme.spacing(3),
		},
		select: {
			color: theme.palette.common.black,
			fontSize: '11pt',
		},
		nameContainer: {
			marginBottom: theme.spacing(3),
		},
		formSection: {
			padding: '16px 0',
		},
		labelSpacing: {
			padding: theme.spacing(0, 0, 2, 0),
		},
		radioRow: {
			marginBottom: theme.spacing(1),
		},
		grey: {
			color: theme.palette.text.secondary,
		},
	});

const mapStateToProps = ({ lang }: ReduxState): StateToProps => {
	return {
		lang,
	};
};

export default withStyles(styles)(connect(mapStateToProps, {})(EarlyOrLateDialog));
